import React, { useCallback, useEffect } from 'react';
import { injectIntl, defineMessages, FormattedMessage } from 'react-intl';
import clsx from 'clsx';
import _map from 'lodash/map';
import _pickBy from 'lodash/pickBy';
import { colors } from '@lifesize/ux-assets';
import { ReactComponent as HandIcon } from '@lifesize/ux-assets/vectors/svgIcons/hand.svg';
import { ReactComponent as PersonPlusIcon } from '@lifesize/ux-assets/vectors/svgIcons/person-plus.svg';
import { ReactComponent as ChatIcon } from '@lifesize/ux-assets/vectors/svgIcons/chat.svg';
import { ReactComponent as CancelIcon } from '@lifesize/ux-assets/vectors/svgIcons/cancel.svg';
import { ReactComponent as CollaborationIcon } from '@lifesize/ux-assets/vectors/svgIcons/collaboration-tools.svg';
import { ReactComponent as SettingsIcon } from '@lifesize/ux-assets/vectors/svgIcons/settings.svg';
import { ReactComponent as InfoIcon } from '@lifesize/ux-assets/vectors/svgIcons/information-outline.svg';
import { ReactComponent as PeopleIcon } from '@lifesize/ux-assets/vectors/svgIcons/people.svg';
import { makeStyles, Theme } from '@material-ui/core';
import { fade } from '@material-ui/core/styles';
import InCallChatContainer from 'containers/InCall/InCallChat/InCallChatContainer';
import AddParticipantDrawer from 'containers/AddParticipantDrawer/AddParticipantDrawerContainer';
import CallDetailsDrawer from 'containers/InCall/InCallCallDetails/InCallCallDetailsContainer';
import CollaborationToolsDrawer from 'containers/InCall/InCallCollaboration/InCallCollaborationContainer';
import Tooltip from 'components/Tooltip/Tooltip';
import Badge from 'components/Badge/Badge';
import {
  CALL_DETAILS_DRAWER,
  CHAT_DRAWER,
  COLLABORATION_DRAWER,
  PARTICIPANTS_DRAWER
} from 'constants/InCall/InCallDrawer';
import { ADD_PARTICIPANT_DRAWER } from 'constants/AddParticipantDrawer';
import InCallParticipantList from './InCallParticipantList/InCallParticipantList';

/**
 * This component will follow a similar paradigm to the FullScreenModal.
 * Subcomponents will be passed to the InCallDrawer. This will likely be a
 * separate child component of this holder, with the current contents being split
 * between drawer controls and drawercomponent.
 */

interface Props {
  availableDrawers: any[];
  btnNotificationRaisedHand: any;
  checkForCrossGroup: Boolean;
  children: React.ReactNode;
  clearRemoteGroupUuid: Function;
  extension: string;
  intl: { formatMessage: Function };
  isGuest: boolean;
  onCloseClicked: (event?: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  onToggleClicked: Function;
  openAV: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  raisedHandsCount: number;
  requestRemoteGroupUuid: Function;
  unreadChats: number;
  visibleComponent: string;
}

const messages = defineMessages({
  closeDrawer: { id: 'components.inCall.drawer.closeDrawerLabel', defaultMessage: 'Close Drawer' },
  addParticipantDrawer: {
    id: 'components.inCall.drawer.addParticipantDrawerButton',
    defaultMessage: 'Add a participant'
  },
  callDetailsDrawer: { id: 'components.inCall.drawer.callDetailsDrawerButton', defaultMessage: 'Call details' },
  participantsDrawer: { id: 'components.inCall.drawer.participantsDrawerButton', defaultMessage: 'Participant list' },
  chatDrawer: { id: 'components.inCall.drawer.chatDrawerButton', defaultMessage: 'Chat' },
  collaborationDrawer: {
    id: 'components.inCall.drawer.collaborationDrawerButton',
    defaultMessage: 'Collaboration Tools'
  },
  inCallSettings: { id: 'components.inCall.drawer.inCallSettings', defaultMessage: 'Camera and audio sources' }
});

export const drawerControlsWidth = 48;
export const drawerContentWidth = 256; // total width = 304px (per Amanda)

export const InCallDrawerHolder = (props: Props) => {
  const classes = useStyles({});
  const { btnNotificationRaisedHand, intl, raisedHandsCount, unreadChats, visibleComponent } = props;

  useEffect(() => {
    // componentDidMount
    if (!props.isGuest) {
      if (props.checkForCrossGroup) {
        props.requestRemoteGroupUuid(props.extension);
      }
    }

    return function componentWillUnmount() {
      props.clearRemoteGroupUuid();
      props.onCloseClicked();
    };
    // note: an empty dependency array indicates that the effect is only run once (equivalent to componentDidMount() & [componentWillUnmount()])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const buttons = useCallback(() => {
    const buttonGenerator = (keys) => {
      const allButtons = {
        [ADD_PARTICIPANT_DRAWER]: {
          className: '',
          dataTestId: 'add-participant-drawer-btn',
          key: 0,
          label: intl.formatMessage(messages.addParticipantDrawer),
          name: 'addParticipant',
          svg: PersonPlusIcon
        },
        [CALL_DETAILS_DRAWER]: {
          className: '',
          dataTestId: 'call-details-drawer-btn',
          key: 1,
          label: intl.formatMessage(messages.callDetailsDrawer),
          name: 'information',
          svg: InfoIcon
        },
        [PARTICIPANTS_DRAWER]: {
          className: '',
          dataTestId: 'participants-drawer-btn',
          key: 2,
          label: intl.formatMessage(messages.participantsDrawer),
          name: 'participants',
          svg: PeopleIcon
        },
        [CHAT_DRAWER]: {
          className: '',
          dataTestId: 'chat-drawer-btn',
          key: 3,
          label: intl.formatMessage(messages.chatDrawer),
          name: 'chat',
          svg: ChatIcon
        },
        [COLLABORATION_DRAWER]: {
          className: '',
          dataTestId: 'collaboration-drawer-btn',
          key: 4,
          label: intl.formatMessage(messages.collaborationDrawer),
          name: 'collaboration',
          svg: CollaborationIcon
        }
      };

      return _pickBy(allButtons, (value, key) => keys.find((providedKey) => key === providedKey));
    };
    // buttons are listed here in order of appearance
    return buttonGenerator(props.availableDrawers);
  }, [intl, props.availableDrawers])();

  const renderCloseButton = useCallback(() => {
    return (
      <button
        id="closeDrawerButton"
        className={classes.closeButton}
        aria-label={intl.formatMessage(messages.closeDrawer)}
        onClick={props.onCloseClicked}
      >
        <CancelIcon data-testid="cancelIcon" />
      </button>
    );
  }, [classes.closeButton, intl, props.onCloseClicked]);

  const renderDrawer = useCallback(() => {
    return (
      <div id="openDrawer" className={classes.drawerContent}>
        <div className="contents">
          <AddParticipantDrawer isVisible={visibleComponent === ADD_PARTICIPANT_DRAWER} />
          <CallDetailsDrawer isVisible={visibleComponent === CALL_DETAILS_DRAWER} />
          <InCallParticipantList />
          <InCallChatContainer isVisible={visibleComponent === CHAT_DRAWER} />
          <CollaborationToolsDrawer isVisible={visibleComponent === COLLABORATION_DRAWER} />
          {renderCloseButton()}
        </div>
      </div>
    );
  }, [classes.drawerContent, renderCloseButton, visibleComponent]);

  const getParticipantsTooltipLabel = (defaultLabel, notification, raisedHandsCount) => {
    if (notification || raisedHandsCount) {
      return (
        <div className="tooltipLabelWithIcon">
          <HandIcon />
          <div className="tooltipMessage">
            {!!notification ? (
              <FormattedMessage {...notification} />
            ) : (
              <FormattedMessage
                id="components.inCall.drawer.participantsDrawerButton.handsRaised"
                defaultMessage="There {handsRaised, plural, one {is} other {are}} {handsRaised, number} raised {handsRaised, plural, one {hand} other {hands}}"
                values={{ handsRaised: raisedHandsCount }}
              />
            )}
          </div>
        </div>
      );
    }
    return defaultLabel;
  };

  const renderButtonLabel = (button) => {
    const ButtonSVG = button.svg;
    if (button.name !== 'participants') {
      return (
        <>
          <Tooltip label={button.label} location="left" style={{ right: '100%', marginRight: 11 }} />
          <ButtonSVG />
          {button.name === 'chat' && unreadChats && props.visibleComponent !== 'CHAT_DRAWER' ? (
            <Badge count={unreadChats} className={classes.badge} />
          ) : null}
        </>
      );
    }
    return (
      <>
        <Tooltip
          label={getParticipantsTooltipLabel(button.label, btnNotificationRaisedHand, raisedHandsCount)}
          location="left"
          style={{ right: '100%', marginRight: 11 }}
          useAltStyle={!!btnNotificationRaisedHand || !!raisedHandsCount}
          visible={!!btnNotificationRaisedHand}
        />
        <ButtonSVG />
        {raisedHandsCount ? <Badge count={raisedHandsCount} className={clsx(classes.badge, 'raisedHands')} /> : null}
      </>
    );
  };

  return (
    <div id="inCallDrawer" className={clsx(classes.container, !!visibleComponent && 'isOpen')}>
      <div className={classes.drawerControls}>
        {_map(buttons, (button, keyName) => {
          const activeStyle = !!visibleComponent && visibleComponent === keyName && 'isActive';

          return (
            <button
              aria-label={button.label}
              className={clsx(button.className, 'controlButton', activeStyle)}
              data-testid={button.dataTestId}
              key={keyName}
              onClick={() => props.onToggleClicked(keyName)}
            >
              {renderButtonLabel(button)}
            </button>
          );
        })}
        <hr />
        <button
          className={clsx('controlButton', 'settingsButton')}
          data-testid="settings-drawer-btn"
          id="avSettingsButton"
          onClick={props.openAV}
        >
          <SettingsIcon data-testid="settingsIcon" />
          <Tooltip
            label={intl.formatMessage(messages.inCallSettings)}
            location="left"
            style={{ right: '100%', marginRight: 11 }}
          />
        </button>
      </div>
      {renderDrawer()}
    </div>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    alignItems: 'stretch',
    backgroundColor: colors.white,
    display: 'flex',
    flex: '0 0 auto',
    flexDirection: 'row',
    flexWrap: 'nowrap',
    height: '100%',
    /* we are opening and closing the drawer using the right margin instead of the width here since
     * changes to the width affect the height of the chat conversation (which impedes our ability to
     * accurately measure and set the bookmarked scroll position) */
    marginRight: -drawerContentWidth,
    order: 2,
    transition: 'margin .5s ease',
    width: drawerControlsWidth + drawerContentWidth,
    zIndex: 400,
    '& .contents': {
      // add wrapper so that the contents can appear/disappear gracefully on drawer open/close
      display: 'flex',
      flex: '1 1 auto',
      flexDirection: 'column',
      flexWrap: 'nowrap',
      height: '100%',
      position: 'relative',
      opacity: 0,
      transitionProperty: 'opacity',
      // transition properties from shown to hidden (no delay, immediate change)
      transitionDelay: '0s',
      transitionDuration: '0s'
    },
    '&.isOpen': {
      marginRight: 0,
      ' & .contents': {
        opacity: 1,
        // transition properties from hidden to shown (delay, then ease in)
        transitionDelay: '.5s',
        transitionDuration: '.5s'
      }
    }
  },
  closeButton: {
    backgroundColor: 'transparent',
    border: 'none',
    color: colors.eurekaBlack,
    cursor: 'pointer',
    margin: 0,
    outline: 'none',
    padding: theme.spacing(1), // improve the clickable area
    position: 'absolute',
    right: 2,
    top: 2,
    '& svg': {
      height: 16,
      width: 'auto'
    }
  },
  drawerControls: {
    backgroundColor: colors.squidInk,
    display: 'flex',
    flex: '0 0 auto',
    flexDirection: 'column',
    width: drawerControlsWidth,
    height: '100%',
    '& .tooltipLabelWithIcon': {
      alignItems: 'center',
      display: 'flex',
      '& svg': {
        color: colors.agileGreen,
        flex: '0 0 auto',
        height: 36,
        width: 'auto'
      },
      '& .tooltipMessage': {
        flex: '1 0 auto',
        marginLeft: theme.spacing(1)
      }
    },
    '& hr': {
      backgroundColor: fade(colors.white, 0.6),
      height: 1,
      width: '100%',
      marginTop: theme.spacing(1)
    },
    '& .controlButton': {
      border: 'none',
      color: fade(colors.white, 0.6),
      cursor: 'pointer',
      margin: `${theme.spacing(2)}px 0`,
      outline: 'none',
      padding: `${theme.spacing(1)}px 0`,
      position: 'relative',
      width: '100%',
      '& svg': {
        height: 36,
        width: 'auto'
      },
      '&:hover': {
        color: colors.white
      },
      '&.isActive': {
        color: colors.white
      }
    }
  },
  drawerContent: {
    backgroundColor: colors.white,
    display: 'flex',
    flex: '0 0 auto',
    flexDirection: 'column',
    height: '100%',
    padding: 0,
    width: drawerContentWidth
  },
  badge: {
    alignItems: 'center',
    border: 0,
    display: 'flex',
    height: 15,
    justifyContent: 'center',
    position: 'absolute',
    right: 4,
    top: 0,
    zIndex: 1,
    '&.raisedHands': {
      backgroundColor: colors.agileGreen
    },
    '& span': {
      fontWeight: theme.typography.fontWeightBold
    }
  }
}));

export default injectIntl(InCallDrawerHolder);
